import { Connection, DATA_TYPE, ITable } from 'jsstore';
import { initLocalDBConnection } from './idb_helper';

export interface IMoveRecord {
	id?: number; // auto-increment
	batchId?: string;
	bucket: string;
	sourceKey: string;
	destinationKey: string;
	fileName: string;
	status: 'pending' | 'moving' | 'completed' | 'failed';
	createdAt: number;
	updatedAt?: number;
}

const TBL_MOVES: ITable = {
	name: 'Moves',
	columns: {
		id: { primaryKey: true, autoIncrement: true },
		batchId: { dataType: DATA_TYPE.String },
		bucket: { dataType: DATA_TYPE.String },
		sourceKey: { dataType: DATA_TYPE.String },
		destinationKey: { dataType: DATA_TYPE.String },
		fileName: { dataType: DATA_TYPE.String },
		status: { dataType: DATA_TYPE.String },
		createdAt: { dataType: DATA_TYPE.Number },
		updatedAt: { dataType: DATA_TYPE.Number }
	}
};

let connection: Connection | null = null;

export const getMovesDB = async () => {
	if (connection) return connection;
	connection = await initLocalDBConnection();
	await connection.initDb({
		name: 'CloudFileSystemMovesDB',
		tables: [TBL_MOVES]
	});
	return connection;
};

export const addMoveRecord = async (record: Omit<IMoveRecord, 'id'>) => {
	const db = await getMovesDB();
	const result = await db.insert<IMoveRecord>({
		into: 'Moves',
		values: [record],
		return: true
	});
	return (result as IMoveRecord[])[0];
};

export const addMoveRecords = async (records: Omit<IMoveRecord, 'id'>[]) => {
	if (records.length === 0) return [];
	const db = await getMovesDB();
	const result = await db.insert<IMoveRecord>({
		into: 'Moves',
		values: records,
		return: true
	});
	return result as IMoveRecord[];
};

export const updateMoveRecord = async (
	id: number,
	updates: Partial<IMoveRecord>
) => {
	const db = await getMovesDB();
	await db.update({
		in: 'Moves',
		set: {
			...updates,
			updatedAt: Date.now()
		},
		where: {
			id: id
		}
	});
};

export const removeMoveRecord = async (id: number) => {
	const db = await getMovesDB();
	await db.remove({
		from: 'Moves',
		where: {
			id: id
		}
	});
};

export const deleteBatchMoves = async (batchId: string) => {
	const db = await getMovesDB();
	await db.remove({
		from: 'Moves',
		where: {
			batchId: batchId
		}
	});
};

export const getAllBucketMoves = async (bucket?: string) => {
	const db = await getMovesDB();
	if (bucket) {
		return await db.select<IMoveRecord>({
			from: 'Moves',
			where: {
				bucket: bucket
			}
		});
	}
	return await db.select<IMoveRecord>({
		from: 'Moves'
	});
};

export const getPendingMoves = async (bucket?: string) => {
	const db = await getMovesDB();
	const results = bucket
		? await db.select<IMoveRecord>({
				from: 'Moves',
				where: {
					bucket: bucket
				}
			})
		: await db.select<IMoveRecord>({
				from: 'Moves'
			});
	return results.filter((r) => r.status !== 'completed');
};
